import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, fetchReviews, submitReview, formatTT } from "../lib/api";
import ApplyDialog from "../components/ApplyDialog";
import { useCompare } from "../context/CompareContext";
import { Star, ArrowLeft, Check, X, Scale } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const StarRow = ({ value, onChange, size = 4 }) => (
    <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
            <button
                key={`star-${i}`} type="button"
                onClick={onChange ? () => onChange(i) : undefined}
                className={onChange ? "cursor-pointer" : "cursor-default"}
                data-testid={onChange ? `rating-star-${i}` : undefined}
            >
                <Star
                    className={`w-${size} h-${size} ${i <= value ? "fill-[#D1A77E] stroke-[#D1A77E]" : "stroke-[#718096]"}`}
                    strokeWidth={1.5}
                />
            </button>
        ))}
    </div>
);

const ProductDetail = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, title: "", comment: "" });
    const { has, add, remove } = useCompare();

    useEffect(() => {
        fetchProduct(id).then(setProduct);
        fetchReviews(id).then(setReviews);
    }, [id]);

    const onSubmitReview = async (e) => {
        e.preventDefault();
        if (!reviewForm.name || !reviewForm.title || !reviewForm.comment) {
            toast.error("Fill all fields");
            return;
        }
        try {
            await submitReview(id, reviewForm);
            toast.success("Review posted");
            setReviewForm({ name: "", rating: 5, title: "", comment: "" });
            fetchReviews(id).then(setReviews);
            fetchProduct(id).then(setProduct);
        } catch {
            toast.error("Failed to post");
        }
    };

    if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-center">Loading…</div>;

    const inCompare = has(product.id);

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
            <Link to="/products" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#0B192C] mb-6" data-testid="back-link">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5}/> Back to catalog
            </Link>

            <div className="grid lg:grid-cols-12 gap-8">
                {/* Main */}
                <div className="lg:col-span-8">
                    <div className="label-overline text-[#1E3A2B] mb-2">{product.provider}</div>
                    <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight mb-3" data-testid="product-title">{product.name}</h1>
                    <div className="flex items-center gap-4 mb-8">
                        <div className="flex items-center gap-2">
                            <StarRow value={Math.round(product.rating)} />
                            <span className="font-mono-body text-sm">{product.rating?.toFixed(1)}</span>
                            <span className="text-xs text-[#718096]">({product.review_count} reviews)</span>
                        </div>
                    </div>

                    <p className="text-base text-[#4A5568] leading-relaxed mb-10 max-w-2xl">{product.description}</p>

                    {/* Key facts */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0B192C]/10 border border-[#0B192C]/10 mb-10">
                        <Fact label={product.type === "credit_card" ? "APR" : "Interest Rate"} value={`${product.interest_rate}%`} />
                        {product.type === "loan" ? (
                            <>
                                <Fact label="Loan Range" value={`${formatTT(product.min_amount)}–${formatTT(product.max_amount)}`} />
                                <Fact label="Tenure" value={`${product.min_tenure_months}–${product.max_tenure_months} mo`} />
                                <Fact label="Processing Fee" value={`${product.processing_fee_pct}%`} />
                            </>
                        ) : (
                            <>
                                <Fact label="Annual Fee" value={formatTT(product.annual_fee)} />
                                <Fact label="Min. Income" value={`${formatTT(product.min_income)}/mo`} />
                                <Fact label="Min. Credit Score" value={product.min_credit_score} />
                            </>
                        )}
                    </div>

                    {/* Features */}
                    <div className="mb-10">
                        <div className="label-overline text-[#718096] mb-3">Features</div>
                        <ul className="grid sm:grid-cols-2 gap-2">
                            {product.features?.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm">
                                    <Check className="w-4 h-4 mt-0.5 text-[#1E3A2B] shrink-0" strokeWidth={2}/>
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Pros / Cons */}
                    <div className="grid md:grid-cols-2 gap-6 mb-10">
                        <div className="grid-card bg-white p-6">
                            <div className="label-overline text-[#2F855A] mb-3">Strengths</div>
                            <ul className="space-y-2">
                                {product.pros?.map((p) => (
                                    <li key={p} className="flex items-start gap-2 text-sm">
                                        <Check className="w-4 h-4 mt-0.5 text-[#2F855A] shrink-0" strokeWidth={2}/>
                                        {p}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="grid-card bg-white p-6">
                            <div className="label-overline text-[#C53030] mb-3">Trade-Offs</div>
                            <ul className="space-y-2">
                                {product.cons?.map((c) => (
                                    <li key={c} className="flex items-start gap-2 text-sm">
                                        <X className="w-4 h-4 mt-0.5 text-[#C53030] shrink-0" strokeWidth={2}/>
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Eligibility */}
                    <div className="border-l-2 border-[#1E3A2B] pl-4 mb-12">
                        <div className="label-overline text-[#718096] mb-2">Eligibility</div>
                        <p className="text-sm text-[#4A5568] leading-relaxed">{product.eligibility}</p>
                    </div>

                    {/* Reviews */}
                    <section>
                        <h2 className="font-serif-display text-3xl tracking-tight mb-6">Customer Reviews</h2>
                        <div className="space-y-4 mb-8">
                            {reviews.length === 0 ? (
                                <p className="text-sm text-[#4A5568]">No reviews yet. Be the first.</p>
                            ) : reviews.map((r) => (
                                <div key={r.id} className="grid-card bg-white p-5" data-testid={`review-${r.id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <div className="font-mono-body text-sm font-medium">{r.name}</div>
                                            <StarRow value={r.rating} />
                                        </div>
                                        <span className="label-overline text-[#718096]">{new Date(r.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div className="font-serif-display text-lg mt-2">{r.title}</div>
                                    <p className="text-sm text-[#4A5568] mt-1">{r.comment}</p>
                                </div>
                            ))}
                        </div>

                        <form onSubmit={onSubmitReview} className="grid-card bg-white p-6" data-testid="review-form">
                            <div className="font-serif-display text-2xl mb-4">Leave a Review</div>
                            <div className="grid md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <Label className="label-overline">Your Name</Label>
                                    <Input
                                        data-testid="review-name"
                                        value={reviewForm.name}
                                        onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                                        className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body"
                                    />
                                </div>
                                <div>
                                    <Label className="label-overline">Rating</Label>
                                    <div className="mt-2"><StarRow value={reviewForm.rating} onChange={(v) => setReviewForm({ ...reviewForm, rating: v })} /></div>
                                </div>
                            </div>
                            <div className="mb-3">
                                <Label className="label-overline">Title</Label>
                                <Input
                                    data-testid="review-title"
                                    value={reviewForm.title}
                                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body"
                                />
                            </div>
                            <div className="mb-4">
                                <Label className="label-overline">Comment</Label>
                                <Textarea
                                    data-testid="review-comment"
                                    rows={3}
                                    value={reviewForm.comment}
                                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                                    className="mt-1 rounded-none border-[#0B192C]/20 font-mono-body"
                                />
                            </div>
                            <button data-testid="review-submit-btn" type="submit" className="btn-accent px-6 py-2.5 label-overline">Post Review</button>
                        </form>
                    </section>
                </div>

                {/* Sticky CTA */}
                <aside className="lg:col-span-4">
                    <div className="grid-card bg-white p-6 sticky top-24">
                        <div className="label-overline text-[#718096] mb-2">Rate</div>
                        <div className="font-serif-display text-5xl mb-1">{product.interest_rate}%</div>
                        <div className="text-xs text-[#4A5568] mb-6 font-mono-body">{product.type === "credit_card" ? "Annual Percentage Rate" : "Indicative APR"}</div>

                        <ApplyDialog product={product} trigger={
                            <button data-testid="detail-apply-btn" className="btn-accent w-full py-3 label-overline mb-2">
                                Apply Now
                            </button>
                        } />
                        <button
                            data-testid="detail-compare-btn"
                            onClick={() => inCompare ? remove(product.id) : add(product)}
                            className={`w-full py-3 label-overline border flex items-center justify-center gap-2 ${
                                inCompare ? "bg-[#0B192C] text-[#F8F7F4] border-[#0B192C]" : "border-[#0B192C]/30 hover:border-[#0B192C]"
                            }`}
                        >
                            <Scale className="w-3.5 h-3.5" strokeWidth={1.5}/>
                            {inCompare ? "Remove from Compare" : "Add to Compare"}
                        </button>

                        <div className="mt-6 pt-6 border-t border-[#0B192C]/10 text-xs text-[#718096] leading-relaxed">
                            Rates shown are indicative. Final approval and rates are subject to {product.provider}&rsquo;s underwriting.
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

const Fact = ({ label, value }) => (
    <div className="bg-white p-5">
        <div className="label-overline text-[#718096] mb-1">{label}</div>
        <div className="font-mono-body text-xl">{value}</div>
    </div>
);

export default ProductDetail;
